import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';

const AuditTrailViewer = ({ auditLogs, onExportData, sessionLog }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [actionFilter, setActionFilter] = useState('all');
  const [dateRange, setDateRange] = useState('today');
  const [showSessionLog, setShowSessionLog] = useState(false);
  const [selectedLogs, setSelectedLogs] = useState([]);

  const filteredLogs = auditLogs?.filter(log => {
    const matchesSearch = log?.action?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
                         log?.user?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
                         log?.resource?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
                         log?.details?.toLowerCase()?.includes(searchTerm?.toLowerCase());
    
    const matchesAction = actionFilter === 'all' || log?.action === actionFilter;
    
    const logDate = new Date(log?.timestamp);
    const today = new Date();
    const matchesDate = (() => {
      switch (dateRange) {
        case 'today':
          return logDate?.toDateString() === today?.toDateString();
        case 'week':
          const weekAgo = new Date(today?.getTime() - 7 * 24 * 60 * 60 * 1000);
          return logDate >= weekAgo;
        case 'month':
          const monthAgo = new Date(today?.getTime() - 30 * 24 * 60 * 60 * 1000);
          return logDate >= monthAgo;
        case 'all':
          return true;
        default:
          return true;
      }
    })();
    
    return matchesSearch && matchesAction && matchesDate;
  });

  const getActionColor = (action) => {
    switch (action) {
      case 'REPORT_ACCESSED': return 'bg-blue-100 text-blue-800';
      case 'DATA_EXPORTED': return 'bg-green-100 text-green-800';
      case 'REPORT_VERIFIED': return 'bg-green-100 text-green-800';
      case 'REPORT_VIEWED': return 'bg-gray-100 text-gray-800';
      case 'PORTAL_ACCESSED': return 'bg-purple-100 text-purple-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getActionIcon = (action) => {
    switch (action) {
      case 'REPORT_ACCESSED': return 'FileText';
      case 'DATA_EXPORTED': return 'Download';
      case 'REPORT_VERIFIED': return 'CheckCircle';
      case 'REPORT_VIEWED': return 'Eye';
      case 'PORTAL_ACCESSED': return 'LogIn';
      default: return 'Activity';
    }
  };

  const getIntegrityIcon = (integrity) => {
    switch (integrity) {
      case 'VERIFIED': return 'Shield';
      case 'SYSTEM_VERIFIED': return 'ShieldCheck';
      default: return 'Clock';
    }
  };

  const getIntegrityColor = (integrity) => {
    switch (integrity) {
      case 'VERIFIED': return 'text-green-600';
      case 'SYSTEM_VERIFIED': return 'text-blue-600';
      default: return 'text-yellow-600';
    }
  };

  const handleSelectLog = (logId) => {
    setSelectedLogs(prev => 
      prev?.includes(logId) 
        ? prev?.filter(id => id !== logId)
        : [...prev, logId]
    );
  };

  const handleBulkExport = () => {
    const selectedLogData = auditLogs?.filter(log => selectedLogs?.includes(log?.id));
    onExportData?.(selectedLogData, 'json');
  };

  const actionOptions = [
    { value: 'all', label: 'All Actions' },
    { value: 'REPORT_ACCESSED', label: 'Report Accessed' },
    { value: 'DATA_EXPORTED', label: 'Data Exported' },
    { value: 'REPORT_VERIFIED', label: 'Report Verified' },
    { value: 'REPORT_VIEWED', label: 'Report Viewed' },
    { value: 'PORTAL_ACCESSED', label: 'Portal Accessed' }
  ];

  const dateOptions = [
    { value: 'today', label: 'Today' },
    { value: 'week', label: 'Past Week' },
    { value: 'month', label: 'Past Month' },
    { value: 'all', label: 'All Time' }
  ];

  return (
    <div className="space-y-6">
      {/* Controls */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex flex-col lg:flex-row lg:items-center space-y-4 lg:space-y-0 lg:space-x-4">
          <div className="flex-1">
            <div className="relative">
              <Icon name="Search" size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" />
              <input
                type="text"
                placeholder="Search audit logs..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e?.target?.value)}
                className="w-full pl-10 pr-4 py-2 border border-border rounded-md bg-background text-foreground focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-4 lg:flex lg:space-x-4">
            <Select
              value={actionFilter}
              onChange={(e) => setActionFilter(e?.target?.value)}
              options={actionOptions}
              className="min-w-[160px]"
            />
            
            <Select
              value={dateRange}
              onChange={(e) => setDateRange(e?.target?.value)}
              options={dateOptions}
              className="min-w-[140px]"
            />
          </div>
          
          <div className="flex space-x-2">
            <Button 
              variant={showSessionLog ? "default" : "outline"}
              size="sm"
              onClick={() => setShowSessionLog(!showSessionLog)}
              iconName="Clock"
              iconPosition="left"
            >
              Session Log
            </Button>
            
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => onExportData?.(filteredLogs, 'csv')}
              iconName="Download"
              iconPosition="left"
            >
              Export All
            </Button>
          </div>
        </div>
        
        {selectedLogs?.length > 0 && (
          <div className="mt-4 pt-4 border-t border-border">
            <div className="flex items-center justify-between">
              <p className="text-sm text-muted-foreground">
                {selectedLogs?.length} logs selected
              </p>
              <div className="flex space-x-2">
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={handleBulkExport}
                  iconName="Download"
                  iconPosition="left"
                >
                  Export Selected
                </Button>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => setSelectedLogs([])}
                  iconName="X"
                  iconPosition="left"
                >
                  Clear
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
      {/* Audit Log Entries */}
      <div className="bg-card border border-border rounded-lg overflow-hidden">
        <div className="p-6 border-b border-border">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-foreground">
              {showSessionLog ? 'Current Session Activity' : 'Audit Trail'}
            </h3>
            <div className="flex items-center space-x-2">
              <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
              <span className="text-xs text-green-600 font-medium">Live Monitoring</span>
            </div>
          </div>
        </div>
        
        <div className="divide-y divide-border max-h-96 overflow-y-auto">
          {(showSessionLog ? sessionLog : filteredLogs)?.map((log, index) => (
            <div key={log?.id || index} className="p-4 hover:bg-muted/50">
              <div className="flex items-start space-x-3">
                <input
                  type="checkbox"
                  checked={selectedLogs?.includes(log?.id)}
                  onChange={() => handleSelectLog(log?.id)}
                  className="mt-1 w-4 h-4 text-primary bg-background border-border rounded focus:ring-primary"
                />
                
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${getActionColor(log?.action)}`}>
                  <Icon name={getActionIcon(log?.action)} size={14} />
                </div>
                
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-1">
                        <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${getActionColor(log?.action)}`}>
                          {log?.action?.replace(/_/g, ' ')}
                        </span>
                        {log?.integrity && (
                          <div className={`flex items-center space-x-1 ${getIntegrityColor(log?.integrity)}`}>
                            <Icon name={getIntegrityIcon(log?.integrity)} size={12} />
                            <span className="text-xs font-medium">{log?.integrity}</span>
                          </div>
                        )}
                      </div>
                      
                      <p className="text-sm font-medium text-foreground mb-1">
                        {log?.resource || log?.details || 'System Activity'}
                      </p>
                      
                      <p className="text-xs text-muted-foreground">
                        {log?.details}
                      </p>
                      
                      <div className="flex items-center space-x-4 mt-2 text-xs text-muted-foreground">
                        <div className="flex items-center space-x-1">
                          <Icon name="User" size={12} />
                          <span>{log?.user}</span>
                        </div>
                        
                        {log?.ipAddress && (
                          <div className="flex items-center space-x-1">
                            <Icon name="Wifi" size={12} />
                            <span>{log?.ipAddress}</span>
                          </div>
                        )}
                        
                        {log?.userAgent && (
                          <div className="flex items-center space-x-1">
                            <Icon name="Monitor" size={12} />
                            <span>{log?.userAgent?.substring(0, 20)}...</span>
                          </div>
                        )}
                      </div>
                    </div>
                    
                    <div className="text-right">
                      <time className="text-xs text-muted-foreground">
                        {new Date(log?.timestamp)?.toLocaleString()}
                      </time>
                      {log?.sessionId && (
                        <p className="text-xs text-muted-foreground mt-1 font-mono">
                          {log?.sessionId}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      {(showSessionLog ? sessionLog : filteredLogs)?.length === 0 && (
        <div className="bg-card border border-border rounded-lg p-12 text-center">
          <Icon name="Activity" size={48} className="text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-foreground mb-2">No Activity Found</h3>
          <p className="text-muted-foreground">
            No audit entries match your current filter criteria.
          </p>
        </div>
      )}
    </div>
  );
};

export default AuditTrailViewer;